<template>
  <DropdownContainer
    ref="containerRef"
    v-model:open="isOpen"
    :size
    :width
    :items="computedItems"
    :dropdownType
    :readonly
    :alertLabel
    :selectedLabel
    :placeholder
    :deletable
    :disabled="!computedItems?.length || disabled"
    :alertType
    :force-value
    :read-only-reason
    dropdownMode="single"
    @on-key-down="onKeyDown"
    @deleting="dropdownKey = props.forceValue ? dropdownKey : undefined"
  >
    <template #dropdown-items>
      <BasicDropdownItem
        v-for="(item, index) in computedItems"
        :key="index"
        :id="makeId(index)"
        :label="item.label"
        :iconName="item.iconName"
        :size
        :width
        :active="dropdownKey === item.id"
        :highlighted="selectIndex === index"
        :disabled="item.disabled"
        :disabledReason="item.disabledReason"
        @click="selectItem(item)"
      />
    </template>
  </DropdownContainer>
</template>

<script setup lang="ts" generic="TDropdownItem = DropdownItem, TDropdownKey extends DropdownId = DropdownId">
  import { computed, ref } from 'vue'
  import type { DropdownProps, DropdownItem, DropdownId } from '@designSystem/components'
  import { useDropdownMenuHandler } from '@designSystem/components/wrapper/dropdownContainer/useDropdownMenuHandler'
  import { useDropdownNavigation } from '@designSystem/components/wrapper/dropdownContainer/useDropdownNavigation'

  const props = withDefaults(defineProps<DropdownProps<TDropdownItem>>(), {
    placeholder: 'Sélectionnez un élément',
    forceValue: false,
    deletable: true,
  })

  const dropdownKey = defineModel<TDropdownKey>()
  const containerRef = ref<{ close: () => void } | null>(null)
  const isOpen = ref(false)

  const { computedItems } = useDropdownMenuHandler<TDropdownItem>(
    props.items,
    props.keyId,
    props.keyLabel,
    props.keyIconName,
  )

  const { selectIndex, makeId, handleArrowDownKey, handleArrowUpKey, handleTab } = useDropdownNavigation(
    computedItems,
    isOpen,
  )

  const selectItem = (item: DropdownItem) => {
    if (props.readonly || item.disabled) return
    if (props.forceValue !== true && dropdownKey.value === item.id) {
      dropdownKey.value = undefined
    } else {
      dropdownKey.value = item.id as TDropdownKey
    }
    containerRef.value?.close()
  }

  const selectedLabel = computed(() => {
    if (dropdownKey.value === null || dropdownKey.value === undefined) return ''
    const item = computedItems.value.find((item) => item.id === dropdownKey.value)
    return item ? item.label : ''
  })

  const onKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault()
        if (!isOpen.value) {
          isOpen.value = true
        } else {
          const item = computedItems.value[selectIndex.value]
          if (item && !item.disabled) {
            selectItem(item)
          }
        }
        break
      case 'ArrowDown':
        event.preventDefault()
        handleArrowDownKey()
        break
      case 'ArrowUp':
        event.preventDefault()
        handleArrowUpKey()
        break
      case 'Tab':
        handleTab()
        break
      default:
        break
    }
  }
</script>
